import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild, OnChanges } from '@angular/core';
import { CommonModule, NgFor } from '@angular/common';
import { CdkPortal, PortalModule } from '@angular/cdk/portal';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, Observable, map } from 'rxjs';
import {
	ButtonComponent,
	ModalOverlayService,
	ModalOverlayRef,
	SlottedModalComponent,
	SvgIconComponent,
	SVGIcon,
	ModalOverlayState,
} from '@woolworthsnz/styleguide';

export interface Option {
	value: string | number | boolean;
	text: string;
}

@Component({
	selector: 'search-single-select',
	standalone: true,
	imports: [CommonModule, PortalModule, FormsModule, NgFor, ButtonComponent, SlottedModalComponent, SvgIconComponent],
	templateUrl: './search-single-select.component.html',
	styleUrls: ['./search-single-select.component.scss'],
})
export class SearchSingleSelectComponent implements OnChanges {
	@Input() name: string;
	@Input() options: Array<Option> = [];
	@Input() currentOptionValue = '';
	@Input() prefixTitle = '';
	@Input()
	optionsRevealApproach: 'dropdown' | 'modal' = 'dropdown';

	@Output() selectChange = new EventEmitter<string>();

	@ViewChild('selectTrigger', { read: ElementRef }) selectTrigger: ElementRef<HTMLElement>;
	@ViewChild('selectModal') selectOverlayTemplate: CdkPortal;
	@ViewChild('selectDropdown') selectDropdownTemplate: CdkPortal;

	nextOptionValue = '';
	private _areOptionsShowing$ = new BehaviorSubject<boolean>(false);
	private _selectModalRef?: ModalOverlayRef;
	private _selectDropdownRef?: ModalOverlayRef;

	constructor(private modalOverlayService: ModalOverlayService) {}

	get currentOptionText(): string {
		return this.options.find(({ value }) => value === this.currentOptionValue)?.text ?? '';
	}

	get statusIcon(): Observable<SVGIcon> {
		return this._areOptionsShowing$.pipe(map((up) => this.getChevronIcon(up)));
	}

	trackByOptionValueFn = (_: number, option: Option): string | number | boolean => option.value;

	showOptions(): void {
		this.nextOptionValue = this.currentOptionValue;

		if (this.optionsRevealApproach === 'dropdown') {
			this.modalOverlayService.setState({
				element: this.selectTrigger,
				isConnectedElement: true,
				backdropClass: 'overlay--transparent',
				scrollStrategy: 'block',
				width: 'auto',
				maxWidth: 224,
			});

			this._selectDropdownRef = this.modalOverlayService.open({
				templateRef: this.selectDropdownTemplate,
				skipTracking: true,
			});

			this._selectDropdownRef?.detachments().subscribe(() => {
				this._areOptionsShowing$.next(false);
			});
		} else {
			this.modalOverlayService.setState({
				isConnectedElement: false,
			});
			this._selectModalRef = this.modalOverlayService.open({
				templateRef: this.selectOverlayTemplate,
				skipTracking: true,
			});

			this._selectModalRef?.detachments().subscribe(() => {
				this._areOptionsShowing$.next(false);
			});
		}

		this._areOptionsShowing$.next(true);
	}

	getChevronIcon(up: boolean): SVGIcon {
		return up ? SVGIcon.ChevronTop : SVGIcon.ChevronBottom;
	}

	onCloseSorterModal(): void {
		this.closeAllModalAndDropdown();
	}

	applyChange(): void {
		if (this.nextOptionValue !== this.currentOptionValue) {
			this.selectChange.emit(this.nextOptionValue);
		}
		this.closeAllModalAndDropdown();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.hasOwnProperty('optionsRevealApproach')) {
			this.closeAllModalAndDropdown();
		}
	}

	public closeAllModalAndDropdown(): void {
		this._selectDropdownRef?.close();
		this._selectModalRef?.close();
		this.modalOverlayService.setState(new ModalOverlayState());
	}
}
